import React, { FC, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Button, Typography, Stack, IconButton } from '@mui/material';
import { Close as CloseIcon, Business, Person } from '@mui/icons-material';
import { useTheme } from 'hooks';
import { tokens } from 'theme';

interface LoginSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectEverbeat: () => void;
  onSelectPartner: () => void;
}

export const LoginSelectionModal: FC<LoginSelectionModalProps> = ({
  open,
  onClose,
  onSelectEverbeat,
  onSelectPartner,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleSelectEverbeat = useCallback(() => {
    onClose();
    onSelectEverbeat();
  }, [onClose, onSelectEverbeat]);

  const handleSelectPartner = useCallback(() => {
    onClose();
    onSelectPartner();
  }, [onClose, onSelectPartner]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='h6' component='div' fontWeight='bold'>
          Select Login Type
        </Typography>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 3 }}>
          <Typography variant='body1' color='text.secondary' align='center' sx={{ mb: 4 }}>
            Please select how you would like to log in
          </Typography>
          <Stack spacing={2}>
            <Button
              fullWidth
              variant='outlined'
              size='large'
              onClick={handleSelectEverbeat}
              startIcon={<Person />}
              sx={{
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                justifyContent: 'flex-start',
                pl: 4,
                borderColor: colors.primary[400],
                '&:hover': {
                  borderColor: colors.primary[600],
                  backgroundColor: colors.grey[50],
                },
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  Everbeat Account
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Login with your personal Everbeat account
                </Typography>
              </Box>
            </Button>

            <Button
              fullWidth
              variant='outlined'
              size='large'
              onClick={handleSelectPartner}
              startIcon={<Business />}
              sx={{
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                justifyContent: 'flex-start',
                pl: 4,
                borderColor: colors.primary[400],
                '&:hover': {
                  borderColor: colors.primary[600],
                  backgroundColor: colors.grey[50],
                },
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  Partner Account
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Login with your partner credentials
                </Typography>
              </Box>
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
